import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { Button } from '@frontend/pole-ui/lib/components/Button'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { checkUserExistByPhone, useGetLegalDocuments } from '@/api/kubik/monolith'
import type { IConsultingFormValues } from '@/components/composed/ConsultingForm/types'
import { createConsultingRequestBody } from '@/components/composed/ConsultingForm/utils'
import { useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { Checkbox, SliderValueMoney, TextInput } from '@/components/ui/ReactHookFormFields'
import { getUser } from '@/logic/auth/reducer'
import type { IApplyingFinancingOfferRequestBody } from '@/logic/financing/interfaces'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferRoute } from '@/types'
import type { ILegalDocument } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString, normalizePhoneNumber } from '@/utils/formatUtils'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { LOAN_AMOUNT_SLIDER_CONFIG } from './constants'
import { messages } from './messages'
import type { IFundingBannerFormProps, TFundingBannerConsultingForm } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  user: getUser()(state),
})

export const FundingBannerForm = (props: IFundingBannerFormProps) => {
  const { phoneNumber, onOpenLoginDialog, onCloseLoginDialog, onSendRequest, onOpenRegistrationForm } = props

  const { user } = useSelector(mapState)
  const { isMobile } = useDeviceType()

  const { data: getLegalDocumentsResult } = useGetLegalDocuments()
  const legalDocuments = (getLegalDocumentsResult?.data ?? []) as ILegalDocument[]

  const initialValues = useMemo<TFundingBannerConsultingForm>(
    () => ({
      phoneNumber,
      loanAmount: LOAN_AMOUNT_SLIDER_CONFIG.minValue,
      agreement: false,
    }),
    [phoneNumber],
  )

  const settingsSliderRequiredAmount = useMemo(
    () => ({ min: LOAN_AMOUNT_SLIDER_CONFIG.minValue, max: LOAN_AMOUNT_SLIDER_CONFIG.maxValue, step: LOAN_AMOUNT_SLIDER_CONFIG.step }),
    [],
  )

  const { control, handleSubmit } = useForm({
    values: initialValues,
    defaultValues: {
      phoneNumber: '',
      loanAmount: LOAN_AMOUNT_SLIDER_CONFIG.minValue,
      agreement: false,
    },
  })

  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()

  const sendLostData = (data: Partial<IApplyingFinancingOfferRequestBody>) => {
    const productId = data.productId ?? FundingProduct.MARKETPLACE

    const defaultLoanAmount = {
      value: LOAN_AMOUNT_SLIDER_CONFIG.minValue,
      uom: 'RUB',
    }

    const body = {
      agreement: true,
      email: data.email ?? '',
      phone: data.phone ?? '',
      comment: messages.form.comment,
      loanAmount: data.loanAmount ?? defaultLoanAmount,
      productId,
    }

    applyLostFinancingOffer({ body })
  }

  const onSubmit = async (formValues: TFundingBannerConsultingForm) => {
    const values: IConsultingFormValues = {
      phone: formValues.phoneNumber,
      loanAmount: String(formValues.loanAmount),
      purpose: null,
      agreement: true,
    }
    const body = createConsultingRequestBody({ values, user, productId: FundingProduct.MARKETPLACE })

    if (isAuthenticated) {
      onSendRequest(values.phone, formValues.loanAmount)

      return
    }

    const checkUserResult = await checkUserExistByPhone({ phone: normalizePhoneNumber(values.phone) })

    if (checkUserResult.data?.exist) {
      onOpenLoginDialog(values.phone, formValues.loanAmount)

      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: normalizePhoneNumber(values.phone) },
        onCloseDialog: () => {
          onCloseLoginDialog(values.phone)
        },
        onCloseModal: () => sendLostData(body),
      })
    } else {
      onOpenRegistrationForm(values.phone, formValues.loanAmount)
    }
  }

  return (
    <div className="fundingBannerForm">
      <form className="fundingBannerForm__wrapper">
        <h2 className="fundingBannerForm__title">{messages.title}</h2>
        <SliderValueMoney
          className="fundingBannerForm__slider"
          name="loanAmount"
          settings={settingsSliderRequiredAmount}
          control={control}
          pointsCount={3}
          rules={{ required: messages.form.phone.errors.required }}
        />
        <TextInput
          className="fundingBannerForm__phone"
          name="phoneNumber"
          type="tel"
          placeholder={messages.form.phone.placeholder}
          rules={{ required: messages.form.phone.errors.required }}
          control={control}
          shouldBeTouchedBeforeError={false}
          isReadonly={isAuthenticated}
          isRequired
        />

        <div className="fundingBannerForm__agreement">
          <Checkbox
            control={control}
            id="agreement"
            name="agreement"
            isMobile={isMobile}
            rules={{ required: messages.agreement.required }}
            errorClassName="fundingBannerForm__agreementError"
          >
            <div>
              {formatLocalizedString(messages.agreement.text, {
                agreement: (
                  <CommonLink
                    url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.personalDataConsent)}
                    shouldOpenInNewWindow
                    className="underline underline_green color_green"
                  >
                    {messages.agreement.agreement}
                  </CommonLink>
                ),
                policy: (
                  <CommonLink
                    url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)}
                    shouldOpenInNewWindow
                    className="underline underline_green color_green"
                  >
                    {messages.agreement.policy}
                  </CommonLink>
                ),
              })}
            </div>
          </Checkbox>
        </div>

        <div className="fundingBannerForm__actions">
          <Button
            type="button"
            className="fundingBannerForm__submitButton"
            modifiers={['green', 'full-width-mobile']}
            onClick={handleSubmit(onSubmit)}
          >
            {messages.form.submitButton}
          </Button>
          <CommonLink className="color_green underline underline_green fundingBannerForm__link" url={EFinancingOfferRoute.MFOPage}>
            {messages.form.moreButton}
          </CommonLink>
        </div>
      </form>
    </div>
  )
}
